import { X, Smartphone, Share, PlusSquare, Bookmark, Download, Monitor } from 'lucide-react';
import { backdropClose } from '../utils/backdropClose';

interface PwaInstallModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function PwaInstallModal({ isOpen, onClose }: PwaInstallModalProps) {
    if (!isOpen) return null;

    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    const steps = isIOS ? [
        {
            icon: <Share className="w-5 h-5 text-blue-600" />,
            text: "Tap the 'Share' button in Safari's bottom toolbar."
        },
        {
            icon: <PlusSquare className="w-5 h-5 text-gray-600" />,
            text: "Scroll down and select 'Add to Home Screen'."
        },
        {
            icon: <PlusSquare className="w-5 h-5 text-blue-600" />,
            text: "Tap 'Add' in the top right corner to finish."
        }
    ] : isAndroid ? [
        {
            icon: <Monitor className="w-5 h-5 text-gray-600" />,
            text: "Tap the three dotsMenu in the top right corner of Chrome."
        },
        {
            icon: <Download className="w-5 h-5 text-emerald-600" />,
            text: "Select 'Install app' or 'Add to Home screen'."
        },
        {
            icon: <PlusSquare className="w-5 h-5 text-emerald-600" />,
            text: "Confirm by tapping 'Install' or 'Add' in the popup."
        }
    ] : [
        {
            icon: <Monitor className="w-5 h-5 text-gray-600" />,
            text: "Look for the 'Install' icon in the right side of the URL bar."
        },
        {
            icon: <Download className="w-5 h-5 text-emerald-600" />,
            text: "Click it and select 'Install' in the confirmation popup."
        }
    ];

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
            {...backdropClose(onClose)}
        >
            <div
                className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-8 duration-500"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-8 text-white relative">
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 shadow-xl border border-white/20">
                        <Smartphone className="w-8 h-8" />
                    </div>

                    <h2 className="text-2xl font-black tracking-tight leading-tight">
                        Install Gympe App
                    </h2>
                    <p className="text-emerald-50 text-sm mt-2 font-medium opacity-90">
                        Add Gympe to your home screen for quick access and a better experience.
                    </p>
                </div>

                {/* Content (Notion-like) */}
                <div className="p-8 bg-gray-50/50">
                    <div className="space-y-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                            Installation Guide for {isIOS ? 'iPhone/iPad' : isAndroid ? 'Android' : 'Desktop'}
                        </h3>

                        <div className="space-y-4">
                            {steps.map((step, index) => (
                                <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm group hover:border-emerald-200 transition-colors">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center font-black text-xs text-gray-400 group-hover:bg-emerald-50 group-hover:text-emerald-600 transition-colors">
                                        {index + 1}
                                    </div>
                                    <div className="flex-shrink-0 mt-1">
                                        {step.icon}
                                    </div>
                                    <p className="text-sm font-bold text-gray-700 leading-snug">
                                        {step.text}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Premium Tip */}
                        <div className="mt-8 p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-sm border border-emerald-100">
                                <Bookmark className="w-5 h-5 text-emerald-600" />
                            </div>
                            <p className="text-[11px] font-bold text-emerald-800 leading-tight">
                                Once installed, Gympe will work like a native app, taking up the full screen.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-white border-t border-gray-100 flex justify-center">
                    <button
                        onClick={onClose}
                        className="px-10 py-3 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all active:scale-95 shadow-lg shadow-gray-200"
                    >
                        Got it, thanks!
                    </button>
                </div>
            </div>
        </div>
    );
}
